import { MovementType } from "@/data/movementType";

/**
 * Type for the category option structure
 */
export type CategoryOption = {
  section_name: string;
  fields: string[];
};

/**
 * Default category options used across the application
 */
export const DEFAULT_CATEGORY_OPTIONS: CategoryOption[] = [
  {
    section_name: "other",
    fields: [MovementType.ASSET_LIQUIDATION, MovementType.AMORTIZATION],
  },
  {
    section_name: "account_fees",
    fields: [MovementType.FEES],
  },
  {
    section_name: "loan_flows",
    fields: [
      MovementType.HOME_LOAN,
      MovementType.OTHER_LOAN,
      MovementType.INTEREST,
    ],
  },
  {
    section_name: "revenue",
    fields: [MovementType.DIVIDEND, MovementType.ACCRUED_INTEREST],
  },
  {
    section_name: "unlisted_asset_funds",
    fields: [
      MovementType.CAPITAL_CALL,
      MovementType.CAPITAL_GAIN,
      MovementType.DISTRIBUTION,
      MovementType.CALL_SUBSEQUENT_CLOSING,
      MovementType.TEMPORARY_RETURN_CAPITAL,
      MovementType.DISTRIBUTION_SUBSEQUENT_CLOSING,
      MovementType.RETURN_EXCESS_CALL,
      MovementType.RETURN_EXCESS_DISTRIBUTION,
      MovementType.TRANSFER_FUND_REALLOCATION,
      MovementType.RETURN_EXCESS_TEMPORARY_DISTRIBUTION,
      MovementType.FEEDER_FEE,
    ],
  },
];

export type movementMap = {
  security: Record<string, MovementType[] | Record<string, MovementType[]>>;
  cash: Record<string, MovementType[]>;
  cash_special_case: Record<string, MovementType[]>;
};

export const DEFAULT_MOVEMENT_MAP: movementMap = {
  security: {
    private_equity_fund: {
      pos: [
        MovementType.CAPITAL_CALL,
        MovementType.CALL_SUBSEQUENT_CLOSING,
        MovementType.RETURN_EXCESS_DISTRIBUTION,
        MovementType.TRANSFER_FUND_REALLOCATION,
        MovementType.RETURN_EXCESS_TEMPORARY_DISTRIBUTION,
        MovementType.FEEDER_FEE,
      ],
      neg: [
        MovementType.CAPITAL_GAIN,
        MovementType.DISTRIBUTION,
        MovementType.TEMPORARY_RETURN_CAPITAL,
        MovementType.DISTRIBUTION_SUBSEQUENT_CLOSING,
        MovementType.RETURN_EXCESS_CALL,
        MovementType.TRANSFER_FUND_REALLOCATION,
      ],
    },
    scpi_usufruit: {
      pos: [],
      neg: [MovementType.AMORTIZATION],
    },
    hedge_fund_unlisted: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
    art: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
    building: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
    personal_use_real_estate: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
    relatives_debt: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
    fonds_euros: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
    other_alternative_non_listed: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
    vehicle: {
      pos: [],
      neg: [MovementType.ASSET_LIQUIDATION, MovementType.RETURN_EXCESS_CALL],
    },
  },
  cash: {
    stock: [MovementType.DIVIDEND],
    stock_fund: [MovementType.DIVIDEND],
    structured_product: [MovementType.ACCRUED_INTEREST],
    fixed_income_structured_product: [MovementType.ACCRUED_INTEREST],
    stock_option: [MovementType.DIVIDEND],
    crypto: [MovementType.DIVIDEND],
    hedge_fund: [MovementType.DIVIDEND],
    hedge_fund_unlisted: [MovementType.DIVIDEND],
    precious_metal: [MovementType.DIVIDEND],
    other_alternative_listed: [MovementType.DIVIDEND],
    other_alternative_non_listed: [MovementType.DIVIDEND],
    cash: [MovementType.ACCRUED_INTEREST],
    monetary_fund: [MovementType.DIVIDEND],
    private_debt: [MovementType.ACCRUED_INTEREST],
    relatives_debt: [MovementType.ACCRUED_INTEREST],
    fixed_income_fund: [MovementType.DIVIDEND],
    bond: [MovementType.ACCRUED_INTEREST],
    home_loan: [MovementType.HOME_LOAN, MovementType.INTEREST],
    other_loan: [MovementType.OTHER_LOAN, MovementType.INTEREST],
    private_equity_direct: [MovementType.DIVIDEND],
    scpi_usufruit: [MovementType.ACCRUED_INTEREST],
  },
  cash_special_case: {
    "*": [MovementType.FEES],
  },
};

export const ASSETCLASS2_WITH_DEFAULT_TRANSFER_TYPE: string[] = [
  "private_equity_direct",
  "private_equity_fund",
  "building",
];
